/**
 * Every destination the homepage can send someone to, in one place.
 *
 * Route strings are not written inline anywhere else. A path repeated across
 * six components is a path that gets changed in five of them.
 *
 * ## Availability is part of the contract
 *
 * Most of this platform is not built yet. A link to an unbuilt module is worse
 * than no link: it looks like a bug to the person clicking it, and it hides
 * from us how much is actually missing. So every destination carries its own
 * availability, the UI reads it, and an unavailable destination renders as a
 * deliberate "coming soon" affordance rather than a dead `href="#"`.
 *
 * When a module ships, its entry flips to `available` and every surface
 * pointing at it starts working at once.
 */

export type Availability = 'available' | 'coming-soon';

export interface Destination {
  readonly path: string;
  readonly label: string;
  readonly availability: Availability;
  /**
   * The key this destination is declared under.
   *
   * Stamped by `withKeys` rather than repeated in each entry, so it cannot
   * drift from the property name. It is what lets a label be edited once in the
   * dashboard and change everywhere the destination is rendered.
   */
  readonly key: string;
}

type DestinationSeed = Omit<Destination, 'key'>;

function live(path: string, label: string): DestinationSeed {
  return { path, label, availability: 'available' };
}

/** Reachable in the information architecture, not yet built behind it. */
function soon(path: string, label: string): DestinationSeed {
  return { path, label, availability: 'coming-soon' };
}

/**
 * Stamps every destination with the name it is declared under.
 *
 * Writing the key into each entry by hand would let it drift from the property
 * name the moment one is renamed, and the key is what the content system uses
 * to find an edited label.
 */
function withKeys<T extends Record<string, DestinationSeed>>(
  seeds: T,
): { readonly [K in keyof T]: Destination } {
  return Object.fromEntries(Object.entries(seeds).map(([key, seed]) => [key, { ...seed, key }])) as {
    readonly [K in keyof T]: Destination;
  };
}

export const routes = withKeys({
  home: live('/', 'Home'),

  // --- Identity. The only consumer flows with a real backend today. --------
  // The website's Log In page (2026-11-01). It used to point at
  // `/identity/login`, which is an API route with no page behind it.
  logIn: live('/login', 'Log in'),
  // There is no sign-up page on the consumer site (`/identity/register` is an
  // API route, not a page); the platform's working registration is the partner
  // portal's Create account screen, which `#register` opens directly.
  signUp: live('/partners/#register', 'Sign up'),
  // Stage 3 (2026-11-01): the Account page is built — logged-out panel, and
  // overview, details, addresses, notifications, privacy and security signed in.
  account: live('/account', 'Your account'),

  // --- The website pages (plan 2026-11-01) ---------------------------------
  // Each path has a page: the frame's logged-out panel, or a plain "being
  // built" panel. None is the finished page yet, so each stays `coming-soon`
  // here — and so every link to it on the frozen homepage stays as it is —
  // until its stage ships and it flips to `available`.
  // Stage 4 (2026-11-01): the Orders page is built — guest orders on this
  // device logged out; list, detail, tracking and cancel signed in. Not linked
  // from the homepage, so Home does not change.
  orders: live('/orders', 'Orders'),
  // Stage 8 (2026-11-01): the Bookings page is built — appointments and online
  // consultations signed in (list, detail, cancel, add to calendar, reply to
  // the prescriber). Not linked from the homepage, so Home does not change.
  bookings: live('/bookings', 'Bookings'),
  // Stage 9 (2026-11-01): the Rewards page is built — the PENNY tab signed in
  // (balance, earn and spend terms from the programme in force, PENNY codes,
  // referrals, offers, history); voucher cards and credit are "Coming soon"
  // (D8). Home links to `penny` (/penny), not here, so Home does not change.
  rewards: live('/rewards', 'Rewards'),
  // Stage 10 (2026-11-01): the Prescriptions page is built — private
  // prescriptions (online consultations and their orders), uploads and repeat
  // requests to a pharmacy, worked from the pharmacy dashboard (D10 phase 1).
  // NHS nomination, NHS number and GP link stay "Coming soon".
  prescriptions: live('/prescriptions', 'Prescriptions'),
  // Stage 6 (2026-11-01): the Checkout page is built — guest (GSL, and P with
  // answers given signed in) or signed in; payment through the platform's own
  // gateway, confirmed only by its webhook. Not linked from the homepage.
  checkout: live('/checkout', 'Checkout'),
  // Stage 12 (2026-11-01): Become a courier is built — public, logged in and
  // out; the sign-up applies through `driver/apply`. Linked only from the
  // website pages' own footer and menu, never from the homepage, so Home does
  // not change.
  couriers: live('/couriers', 'Become a courier'),

  // --- Discovery ----------------------------------------------------------
  // Built in Phase 18: discovery is real, so this destination resolves.
  search: live('/search', 'Search'),
  offers: soon('/offers', 'Offers'),
  // Stage 11 (2026-11-01): the Help page is built, so Support goes there. This
  // is one of the two homepage links the stage changes (header "Support").
  support: live('/help', 'Support'),
  basket: soon('/basket', 'Basket'),
  // Stage 7 (2026-11-01): Places near you is built — the storefront directory
  // for a postcode, logged in and out. Not linked from the homepage.
  nearby: live('/nearby', 'Nearby'),

  // --- Shop ---------------------------------------------------------------
  prescriptionMedicines: soon('/shop/prescription-medicines', 'Prescription Medicines'),
  overTheCounter: soon('/shop/over-the-counter', 'Over the Counter'),
  vitamins: soon('/shop/vitamins-supplements', 'Vitamins & Supplements'),
  healthAndBaby: soon('/shop/health-baby', 'Health & Baby'),
  healthDevices: soon('/shop/health-devices', 'Health & Devices'),
  personalCare: soon('/shop/personal-care', 'Personal Care'),
  petHealth: soon('/shop/pet-health', 'Pet Health'),
  medicinesPharmacy: soon('/shop/medicines-pharmacy', 'Medicines & Pharmacy'),
  pharmacyMedicines: soon('/shop/pharmacy-medicines', 'Pharmacy Medicines'),
  everydayHealth: soon('/shop/everyday-health', 'Everyday Health'),

  // --- Services -----------------------------------------------------------
  consultations: soon('/services/consultations', 'Online Consultations'),
  labTests: soon('/services/lab-tests', 'Lab Tests at Home'),
  healthChecks: soon('/services/health-checks', 'Health Checks'),
  prescriptionService: soon('/services/prescriptions', 'Prescription Service'),
  repeatOrders: soon('/services/repeat-orders', 'Repeat Orders'),
  healthPlans: soon('/services/health-plans', 'Health Plans & Memberships'),
  healthSupport: soon('/services/health-support', 'Health Support'),
  appointments: soon('/services/appointments', 'Appointments'),
  pharmacyServices: soon('/services/pharmacy', 'Pharmacy Services'),
  careServices: soon('/services/care', 'Care Services'),
  beautyWellness: soon('/services/beauty-wellness', 'Beauty & Wellness'),

  // --- Fulfilment ---------------------------------------------------------
  delivery: soon('/delivery', 'Delivery'),
  collection: soon('/collection', 'Collection'),
  tracking: soon('/orders/tracking', 'Delivery Tracking'),
  send: soon('/send', 'Just Treated Send'),

  // --- Business / partners ------------------------------------------------
  // Stage 13: both are the Partner with us page, whose form lists a pharmacy or
  // any of the healthcare business types the platform onboards.
  listPharmacy: live('/partner-with-us', 'List your pharmacy'),
  listHealthcareBusiness: live('/partner-with-us', 'List your healthcare business'),
  joinClinician: soon('/business/clinicians', 'Join as a clinician'),
  careWellnessProviders: soon('/business/care-wellness', 'Care & wellness providers'),
  deliveryPartners: soon('/business/delivery-partners', 'Delivery partners'),
  suppliers: soon('/business/suppliers', 'Suppliers & procurement'),
  enterprise: soon('/business/enterprise', 'Enterprise / HQ'),
  partnerPortal: soon('/business/partner-portal', 'Partner portal'),
  apiIntegrations: soon('/business/api', 'API & integrations'),

  // --- Platform surfaces --------------------------------------------------
  pharmacyOs: soon('/pharmacy-os', 'Pharmacy OS'),
  osPrescriptions: soon('/pharmacy-os/prescriptions', 'Prescriptions'),
  osDispensing: soon('/pharmacy-os/dispensing', 'Dispensing'),
  osInventory: soon('/pharmacy-os/inventory', 'Inventory & Stock'),
  osPickPack: soon('/pharmacy-os/pick-pack', 'Barcode, Pick & Pack'),
  osNms: soon('/pharmacy-os/nms', 'NMS'),
  osPatient360: soon('/pharmacy-os/patient-360', 'Patient 360'),
  osPmr: soon('/pharmacy-os/pmr-integrations', 'PMR & Integrations'),
  osCompliance: soon('/pharmacy-os/sop-compliance', 'SOP & Compliance'),
  osProcurement: soon('/pharmacy-os/procurement', 'Procurement'),
  osReports: soon('/pharmacy-os/reports', 'Reports & Insights'),
  penny: soon('/penny', 'PENNY'),
  news: soon('/news', 'News and updates'),

  // --- Company / help -----------------------------------------------------
  about: soon('/about', 'About Us'),
  careers: soon('/careers', 'Careers'),
  // Not `/partners/`: nginx serves the partner portal there.
  // Stage 13 (2026-11-01): Partner with us is built — the application form
  // starts a real provider application. Live, so Home's footer and mega-menu
  // "Partners With Us" link goes live with it (the owner's D1 allowance).
  partnersWithUs: live('/partner-with-us', 'Partners With Us'),
  press: soon('/press', 'Press & Media'),
  contact: soon('/contact', 'Contact Us'),
  // Stage 11 (2026-11-01): the Help page is built — the footer's and mega
  // menu's "Help Centre" on the homepage now open it.
  helpCentre: live('/help', 'Help Centre'),
  deliveryInformation: soon('/help/delivery', 'Delivery Information'),
  returns: soon('/help/returns', 'Returns & Refunds'),
  privacy: soon('/legal/privacy', 'Privacy Policy'),
  terms: soon('/legal/terms', 'Terms & Conditions'),
  accessibility: soon('/legal/accessibility', 'Accessibility'),
  cookies: soon('/legal/cookies', 'Cookie settings'),
  security: soon('/legal/security', 'Security'),
});

export type RouteKey = keyof typeof routes;

/**
 * The Help page opened at one topic — the "Get help" links on Orders,
 * Bookings and Checkout. An unknown topic simply opens the page.
 */
export const helpTopicPath = (topic: 'orders' | 'appt' | 'pay'): string =>
  `${routes.helpCentre.path}?topic=${topic}`;

/**
 * Mobile app store listings.
 *
 * Deliberately absent. The native apps are not published, and a store button
 * pointing at a listing that does not exist is a broken promise on the
 * homepage. The buttons render disabled until these hold real URLs.
 */
export const appStoreListings: {
  readonly ios?: string;
  readonly android?: string;
} = {};
